import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import {
  PushNotifications,
  Token,
  PushNotificationSchema,
  ActionPerformed
} from '@capacitor/push-notifications';
import { TenantService } from './tenant.service';

@Injectable({ providedIn: 'root' })
export class PushNotificationService {

  private readonly pushEnabled = true;

  constructor(
    private router: Router,
    private http: HttpClient,
    private zone: NgZone,
    private tenantService: TenantService
  ) {}

  /**
   * Call this immediately after a successful login on native platforms.
   * Requests permission, registers with FCM, and wires up all listeners.
   */
  async initialize(userId: string): Promise<void> {
    if (!Capacitor.isNativePlatform() || !this.pushEnabled) return;

    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive !== 'granted') {
      console.warn('PushNotifications: permission not granted');
      return;
    }

    await PushNotifications.register();
    this.registerListeners(userId);
  }

  private registerListeners(userId: string): void {

    // FCM / APNs assigned a device token — register it with our backend
    PushNotifications.addListener('registration', (token: Token) => {
      this.sendTokenToBackend(token.value, userId);
    });

    PushNotifications.addListener('registrationError', (err: any) => {
      console.error('PushNotifications registration error:', err);
    });

    // Notification arrives while app is in the foreground
    PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        this.zone.run(() => {
          // The OS suppresses the system banner when the app is foregrounded.
          // Show an in-app banner here via Toastr or IonToast.
          console.log('Foreground notification:', notification.title, notification.body);
          // TODO: inject ToastrService and call toastr.info(notification.body, notification.title)
        });
      }
    );

    // User tapped a notification — deep link into the app
    PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action: ActionPerformed) => {
        this.zone.run(() => {
          const route: string | undefined = action.notification.data?.route;
          if (route) {
            this.router.navigateByUrl(route);
          }
        });
      }
    );
  }

  private sendTokenToBackend(token: string, userId: string): void {
    const cfg = this.tenantService.getConfig();
    if (!cfg) return;

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    this.http.post(
      `${cfg.apiBaseUrl}/Notifications/RegisterDevice`,
      {
        DeviceToken: token,
        UserId: String(userId),
        MarketId: cfg.marketId,
        Platform: Capacitor.getPlatform()   // 'android' | 'ios'
      },
      { headers }
    ).subscribe({
      next: () => console.log('Device token registered with backend successfully.'),
      error: (err) => console.error(
        `Failed to register device token. status=${err?.status} message=${err?.error?.message || err?.message}`
      )
    });
  }

  /**
   * Call on logout to remove this device token from the backend
   * and stop receiving notifications on this device.
   *
   * IMPORTANT: Await this method BEFORE clearing the auth session/token
   * in your logout flow, since the backend call requires a valid auth
   * header (endpoint is [Authorize]-protected).
   *
   * Example:
   *   async logout() {
   *     await this.pushNotificationService.unregister(this.currentUserId);
   *     this.authService.clearSession();
   *     this.router.navigate(['/login']);
   *   }
   */
  async unregister(userId: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    const cfg = this.tenantService.getConfig();
    if (cfg) {
      const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      try {
        await firstValueFrom(
          this.http.post(
            `${cfg.apiBaseUrl}/Notifications/UnregisterDevice`,
            { UserId: String(userId) },
            { headers }
          )
        );
        console.log('Device unregistered from backend successfully.');
      } catch (err: any) {
        console.error(
          `Failed to unregister device. status=${err?.status} message=${err?.error?.message || err?.message}`
        );
      }
    }

    await PushNotifications.unregister();
  }

  /**
   * Remove all delivered notifications from the notification tray.
   * Useful when the user opens a specific screen that was the notification target.
   */
  async clearDeliveredNotifications(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    await PushNotifications.removeAllDeliveredNotifications();
  }

}